import { Check, Copy } from "lucide-react";
import { forwardRef, useCallback, useMemo } from "react";

import { Button } from "@/components/flexkit/Button";
import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import type { WithTrackingId } from "@/types/tracking";
import { haptic } from "@/utils/haptic";

type InputProps = React.ComponentPropsWithoutRef<"textarea"> & {
    copyable?: boolean;
    value: string;
    title?: string;
    id?: string;
} & WithTrackingId &
    PropsWithTestId;
const CopyableTextArea = forwardRef<HTMLTextAreaElement, InputProps>(
    ({ value, className, copyable, title, id, "data-tracking-id": dataTrackingId, ...props }, ref) => {
        const [copied, setCopied] = useTemporaryState(false, 2000);
        const handleCopyToClipboard = useCallback(() => {
            void navigator.clipboard.writeText(value).then(() => {
                haptic("confirm");
                setCopied(true);
            });
        }, [setCopied, value]);

        const rows = useMemo(() => {
            return value.split("\n").length;
        }, [value]);

        return (
            <div className="relative">
                <textarea
                    className={cn(
                        standaloneInputClassName,
                        "h-auto w-full max-w-full resize-none overflow-y-auto whitespace-pre text-wrap pr-12 font-mono text-base",
                        className,
                    )}
                    rows={rows}
                    readOnly
                    value={value}
                    id={id}
                    {...props}
                    ref={ref}
                />
                {copyable && (
                    <div className="absolute inset-y-2 right-2 flex items-start">
                        <Button
                            variant="ghost"
                            type="button"
                            className="h-6 rounded-lg border-none p-1 text-content-tertiary hover:text-content-secondary hover:opacity-100"
                            onClick={handleCopyToClipboard}
                            aria-label={title ?? "Copy to clipboard"}
                            title={title}
                            data-tracking-id={dataTrackingId}
                        >
                            {copied ? <Check className="text-green-500" size={16} /> : <Copy size={16} />}
                        </Button>
                    </div>
                )}
            </div>
        );
    },
);
CopyableTextArea.displayName = "CopyableTextArea";

export { CopyableTextArea };
